import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { teamApi } from './api'

export function useMembers() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'members'),
    queryFn: teamApi.members,
  })
}

export function useRoles() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'roles'),
    queryFn: teamApi.roles,
  })
}

type Kind = 'activate' | 'deactivate' | 'remove'

export function useMemberAction() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, kind }: { id: number; kind: Kind }) => {
      switch (kind) {
        case 'activate': return teamApi.activate(id)
        case 'deactivate': return teamApi.deactivate(id)
        case 'remove': return teamApi.remove(id)
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'members') }),
  })
}

export function useSetMemberRole() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, roleId }: { id: number; roleId: number }) => teamApi.setRole(id, roleId),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'members') }),
  })
}

export function useRolesFull() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'roles', 'full'),
    queryFn: teamApi.rolesFull,
  })
}

export function useRolePermissions() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'roles', 'permissions'),
    queryFn: teamApi.rolePermissions,
  })
}

export function useCreateRole() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.createRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'roles') }),
  })
}

export function useUpdateRole() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: { name?: string; description?: string } }) =>
      teamApi.updateRole(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'roles') }),
  })
}

export function useSetRolePermissions() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({ id, permissions }: { id: number; permissions: string[] }) =>
      teamApi.setRolePermissions(id, permissions),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'roles') }),
  })
}

export function useDeleteRole() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.deleteRole,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'roles') }),
  })
}

export function useInvitations() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'invitations'),
    queryFn: teamApi.invitations,
  })
}

export function useCreateInvitation() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.createInvitation,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'invitations') }),
  })
}

export function useResendInvitation() {
  return useMutation({ mutationFn: teamApi.resendInvitation })
}

export function useRevokeInvitation() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.revokeInvitation,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'invitations') }),
  })
}

export function useApiKeys() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'api-keys'),
    queryFn: teamApi.apiKeys,
  })
}

export function useCreateApiKey() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.createApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'api-keys') }),
  })
}

export function useDeleteApiKey() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.deleteApiKey,
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'api-keys') }),
  })
}

export function useTeamAuditLogs(params: {
  search?: string
  action?: string
  resource_type?: string
  limit?: number
  offset?: number
}) {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(
      tenantId,
      'team',
      'audit',
      params.search ?? '',
      params.action ?? '',
      params.resource_type ?? '',
      params.limit ?? 50,
      params.offset ?? 0,
    ),
    queryFn: () => teamApi.auditLogs(params),
  })
}

export function useAiGatewayLimits() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'ai-gateway', 'limits'),
    queryFn: teamApi.aiGatewayLimits,
  })
}

export function useAiGatewayMe() {
  const tenantId = useTenant()
  return useQuery({
    queryKey: tenantKey(tenantId, 'team', 'ai-gateway', 'me'),
    queryFn: teamApi.aiGatewayMe,
  })
}

export function useSetAiGatewayLimits() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: ({
      roleId,
      body,
    }: {
      roleId: number
      body: { monthlyTokenLimit: number; maxAgents: number; maxWorkflows: number }
    }) => teamApi.setAiGatewayLimits(roleId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'ai-gateway') })
    },
  })
}

export function useDeleteAuditLogs() {
  const qc = useQueryClient()
  const tenantId = useTenant()
  return useMutation({
    mutationFn: teamApi.deleteAuditLogs,
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: tenantKey(tenantId, 'team', 'audit') }),
  })
}
